import { NgTemplateOutlet } from '@angular/common';
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  output,
  inject,
  input,
} from '@angular/core';
import type { UntypedFormGroup } from '@angular/forms';
import { ReactiveFormsModule, UntypedFormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';

import type { QuickEvaluationContext } from '@core/analytics-legacy';
import { QuickEvaluationsAnalytics } from '@core/analytics-legacy';
import type { FullCandidate } from '@core/candidate-types';
import { TranslatePipe } from '@core/i18n-legacy';
import { InterviewResultsActions, isPlacementRatingVisible } from '@core/interview-results-data';
import type { InterviewResult } from '@core/interview-results-types';
import { OfferSelectComponent } from '@core/offers-smart-ui';
import { OfferKind } from '@core/offers-types';
import type { OfferSelectOption } from '@core/offers-ui';
import type { Placement } from '@core/placements-types';
import type { ConnectedOverlayDirective } from '@core/user-interface-legacy';
import {
  ButtonComponent,
  DropdownComponent,
  DropdownHeaderComponent,
  DropdownWidth,
  IconButtonComponent,
} from '@core/user-interface-legacy';
import { generateUuid } from '@core/utils-legacy';

import { InterviewButtonSelectComponent } from '../interview-button-select/interview-button-select.component';
import { InterviewKind } from '../interview-kind';
import { QuickEvaluationContextService } from '../quick-evaluation-context.service';
import {
  QuickEvalOperationType,
  QuickEvaluationNotificationService,
} from '../quick-evaluation-notification.service';

@Component({
  selector: 'rt-add-quick-evaluation-dropdown',
  templateUrl: 'add-quick-evaluation-dropdown.component.html',
  styleUrls: ['./add-quick-evaluation-dropdown.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [QuickEvaluationsAnalytics],
  imports: [
    DropdownComponent,
    ReactiveFormsModule,
    DropdownHeaderComponent,
    NgTemplateOutlet,
    InterviewButtonSelectComponent,
    ButtonComponent,
    OfferSelectComponent,
    TranslatePipe,
    IconButtonComponent,
  ],
})
export class AddQuickEvaluationDropdownComponent {
  private readonly formBuilder = inject(UntypedFormBuilder);
  private readonly quickEvaluationNotificationService = inject(QuickEvaluationNotificationService);
  private readonly router = inject(Router);
  private readonly quickEvaluationsAnalytics = inject(QuickEvaluationsAnalytics);
  private readonly quickEvaluationContext = inject(QuickEvaluationContextService);
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);

  public readonly dropdownRef = input<ConnectedOverlayDirective>(undefined!);
  public readonly candidate = input<FullCandidate>(undefined!);
  public readonly placement = input<Placement>();
  public readonly isActionToolbar = input<boolean>(undefined!);
  public readonly hasAccessToAllJobOffers = input<boolean>(undefined!);
  public readonly quickEvaluationTrigger = input<QuickEvaluationContext>(undefined!);
  public readonly availablePlacements = input<Placement[]>([]);
  public readonly asDropdownTemplate = input<boolean, unknown>(false, {
    transform: booleanAttribute,
  });
  public readonly hasBackAction = input<boolean, unknown>(false, { transform: booleanAttribute });

  public optionsClick = output<void>();
  public closeDialog = output<void>();
  public goBack = output<void>();

  public quickEvaluationForm: UntypedFormGroup;
  public offers: OfferSelectOption[] = [];

  public DropdownWidth = DropdownWidth;

  private ngOnDestroy$: Subject<void> = new Subject();

  public ngOnInit(): void {
    this.quickEvaluationContext
      .findPlacement(this.availablePlacements())
      .pipe(map(placement => placement || this.placement()))
      .subscribe(placement => {
        this.initEvaluationForm(placement);
      });

    this.quickEvaluationForm
      .get('rating')!
      .valueChanges.pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(rating => {
        this.createInterviewResult({ ...this.quickEvaluationForm.value, rating });
        this.close();
      });

    this.quickEvaluationForm
      .get('offerId')!
      .valueChanges.pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(offerId => {
        const stageId = this.candidate().placements.find(
          placement => offerId === placement.offerId,
        )?.stageId;
        this.quickEvaluationForm.get('stageId')!.setValue(stageId || null);
      });
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public createInterviewResult(value: Partial<InterviewResult>): void {
    const offer = this.offers.find(offer => offer.id === value.offerId);
    const offerId =
      ((offer?.kind === OfferKind.Job || this.placement()?.offerId) && value.offerId) || null;
    const talentPoolId =
      ((offer?.kind === OfferKind.TalentPool || this.placement()?.talentPoolId) && value.offerId) ||
      null;

    const data = {
      ...value,
      offerId,
      talentPoolId,
      kind: InterviewKind.QUICK,
    };

    this.showSuccessNotification();

    this.store.dispatch(
      InterviewResultsActions.createOnePessimistic({
        payload: {
          data: {
            value: data,
            candidateId: this.candidate().id,
          },
          correlationId: generateUuid(),
        },
      }),
    );
  }

  public handleClick(): void {
    if (this.isActionToolbar()) {
      this.router.navigate([], {
        queryParams: {
          candidate: this.candidate().id,
          tab: 'interviews',
        },
        queryParamsHandling: 'merge',
      });
    } else {
      this.optionsClick.emit();
    }
    this.close();
  }

  public close(): void {
    this.closeDialog.emit();
    this.dropdownRef().open = false;
  }

  private initEvaluationForm(placement?: Placement): void {
    this.quickEvaluationForm = this.formBuilder.group({
      offerId: placement?.offerId || placement?.talentPoolId,
      stageId: placement?.stageId,
      rating: ['', Validators.required],
      ratingNote: '',
    });
    this.offers = this.availablePlacements().length ? this.filterAndMapOffers() : [];
  }

  private filterAndMapOffers(): OfferSelectOption[] {
    return <OfferSelectOption[]>this.availablePlacements().map(placement => ({
      id: placement && (placement.offer?.id || placement.talentPool?.id)!,
      title: placement && (placement.offer?.title || placement.talentPool?.title)!,
      kind: placement && (placement.offer?.kind || placement.talentPool?.kind)!,
      status: placement && (placement.offer?.status || placement.talentPool?.status)!,
    }));
  }

  private showSuccessNotification(): void {
    this.actions$
      .pipe(ofType(InterviewResultsActions.createOnePessimisticSuccess))
      .pipe(take(1))
      .subscribe(data => {
        const hasPlacementsInCandidate = 'placements' in this.candidate();
        const placementRatingVisible = isPlacementRatingVisible(
          hasPlacementsInCandidate ? (this.candidate().placements ?? []) : [this.placement()!],
          data.payload.data.offerId,
        );
        this.quickEvaluationsAnalytics.trackQuickEvaluate(
          this.quickEvaluationTrigger(),
          !placementRatingVisible,
        );
        this.quickEvaluationNotificationService.success({
          operationType: QuickEvalOperationType.ADD,
          rating: data.payload.data?.rating,
        });
      });
  }
}
